import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";

// ─── Types ───────────────────────────────────────────────────────────────────

interface CrmCustomer {
  id: string;
  customer_no: string;
  first_name: string;
  last_name: string;
  phone: string;
  phone2: string;
  email: string;
  status: "active" | "passive";
  is_vip: boolean;
  dealer_id: string;
  created_at: string;
}

interface CrmAddress {
  id: string;
  customer_id: string;
  country: string;
  city: string;
  district: string;
  region: string;
  address: string;
  flat: string;
  apartment_no: string;
  street: string;
  is_residential: boolean;
  ownership_type: string;
  status: "active" | "passive";
}

interface CrmDevice {
  id: string;
  customer_id: string;
  serial_no: string;
  device_code: string;
  device_name: string;
  brand: string;
  start_date: string;
  warranty_end_date: string;
  extended_warranty: boolean;
  status: "active" | "passive";
}

interface CrmWorkflow {
  id: string;
  customer_id: string;
  category_type: string;
  notification_type: string;
  sub_category: string;
  flow_type: string;
  status_description: string;
  created_at: string;
  serial_no: string;
}

interface CrmNote {
  id: string;
  customer_id: string;
  note: string;
  created_by: string;
  created_at: string;
}

type DetailTab = "info" | "address" | "devices" | "workflow" | "notes";

const TABS: { id: DetailTab; label: string; icon: string }[] = [
  { id: "info",     label: "Müşteri Bilgileri",  icon: "ri-user-line"            },
  { id: "address",  label: "Adres Bilgileri",     icon: "ri-map-pin-line"         },
  { id: "devices",  label: "Cihaz Bilgileri",     icon: "ri-settings-3-line"      },
  { id: "workflow", label: "İş Emirleri",         icon: "ri-clipboard-line"       },
  { id: "notes",    label: "Notlar",              icon: "ri-file-text-line"       },
];

// ─── Component ───────────────────────────────────────────────────────────────

export default function WarehouseCrmPage() {
  const { user } = useAuth();
  const dealerId = user?.dealerId || user?.id || "";

  // List state
  const [customers, setCustomers] = useState<CrmCustomer[]>([]);
  const [loading, setLoading]     = useState(true);
  const [query, setQuery]         = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "passive">("all");

  // Detail state
  const [selected, setSelected]         = useState<CrmCustomer | null>(null);
  const [activeTab, setActiveTab]       = useState<DetailTab>("info");
  const [addresses, setAddresses]       = useState<CrmAddress[]>([]);
  const [devices, setDevices]           = useState<CrmDevice[]>([]);
  const [workflows, setWorkflows]       = useState<CrmWorkflow[]>([]);
  const [notes, setNotes]               = useState<CrmNote[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Add/Edit modals
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showAddDevice,   setShowAddDevice]   = useState(false);
  const [showAddWorkflow, setShowAddWorkflow] = useState(false);
  const [showAddNote,     setShowAddNote]     = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<CrmCustomer | null>(null);
  const [saving, setSaving] = useState(false);

  const emptyCustomerForm = {
    customer_no: "", first_name: "", last_name: "", phone: "", phone2: "", email: "",
    status: "active" as const, is_vip: false,
    // inline device fields
    district: "", address: "",
    device_name: "", warranty_start: "", warranty_end: "", quantity: "",
  };
  const [customerForm, setCustomerForm] = useState(emptyCustomerForm);

  const [deviceForm, setDeviceForm] = useState({ serial_no: "", device_code: "", device_name: "", brand: "Bosch", start_date: "", warranty_end_date: "", extended_warranty: false });
  const [workflowForm, setWorkflowForm] = useState({ category_type: "Bireysel Müşteri", notification_type: "Kurulum", sub_category: "Yetkili Servis Talebi", flow_type: "Kurulum", status_description: "", serial_no: "" });
  const [noteText, setNoteText] = useState("");

  // ── Load customers ──────────────────────────────────────────────────────────
  const loadCustomers = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    try {
      const supabase = getSupabase();
      const { data } = await supabase
        .from("crm_customers")
        .select("*")
        .eq("dealer_id", dealerId)
        .order("created_at", { ascending: false });
      setCustomers((data as CrmCustomer[]) || []);
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  // ── Load customer details ───────────────────────────────────────────────────
  const loadDetail = useCallback(async (customerId: string) => {
    setDetailLoading(true);
    try {
      const supabase = getSupabase();
      const [addrRes, devRes, wfRes, noteRes] = await Promise.all([
        supabase.from("crm_addresses").select("*").eq("customer_id", customerId).order("created_at"),
        supabase.from("crm_devices").select("*").eq("customer_id", customerId).order("start_date", { ascending: false }),
        supabase.from("crm_workflows").select("*").eq("customer_id", customerId).order("created_at", { ascending: false }),
        supabase.from("crm_notes").select("*").eq("customer_id", customerId).order("created_at", { ascending: false }),
      ]);
      setAddresses((addrRes.data as CrmAddress[]) || []);
      setDevices((devRes.data as CrmDevice[]) || []);
      setWorkflows((wfRes.data as CrmWorkflow[]) || []);
      setNotes((noteRes.data as CrmNote[]) || []);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const selectCustomer = (c: CrmCustomer) => {
    setSelected(c);
    setActiveTab("info");
    loadDetail(c.id);
  };

  // ── Filtered list ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return customers.filter((c) => {
      const matchQ = !q || `${c.first_name} ${c.last_name} ${c.phone} ${c.phone2} ${c.email} ${c.customer_no}`.toLowerCase().includes(q);
      const matchS = statusFilter === "all" || c.status === statusFilter;
      return matchQ && matchS;
    });
  }, [customers, query, statusFilter]);

  // ── Save customer ───────────────────────────────────────────────────────────
  const saveCustomer = async () => {
    if (!customerForm.first_name.trim() || !customerForm.last_name.trim()) return;
    setSaving(true);
    try {
      const supabase = getSupabase();
      const custPayload = {
        dealer_id: dealerId,
        customer_no: customerForm.customer_no,
        first_name: customerForm.first_name,
        last_name: customerForm.last_name,
        phone: customerForm.phone,
        phone2: customerForm.phone2,
        email: customerForm.email,
        status: customerForm.status,
        is_vip: customerForm.is_vip,
      };

      let customerId = editingCustomer?.id || "";
      if (editingCustomer) {
        await supabase.from("crm_customers").update(custPayload).eq("id", editingCustomer.id);
      } else {
        const { data } = await supabase.from("crm_customers").insert(custPayload).select("id").single();
        customerId = (data as any)?.id || "";
      }

      // Save address if provided
      if (customerId && (customerForm.address || customerForm.district)) {
        if (editingCustomer) {
          const { data: existing } = await supabase.from("crm_addresses").select("id").eq("customer_id", customerId).maybeSingle();
          if (existing) {
            await supabase.from("crm_addresses").update({ district: customerForm.district, address: customerForm.address, city: "Antalya", country: "Türkiye" }).eq("id", (existing as any).id);
          } else {
            await supabase.from("crm_addresses").insert({ customer_id: customerId, district: customerForm.district, address: customerForm.address, city: "Antalya", country: "Türkiye" });
          }
        } else {
          await supabase.from("crm_addresses").insert({ customer_id: customerId, district: customerForm.district, address: customerForm.address, city: "Antalya", country: "Türkiye" });
        }
      }

      // Save device if provided
      if (customerId && customerForm.device_name && !editingCustomer) {
        await supabase.from("crm_devices").insert({
          customer_id: customerId,
          device_name: customerForm.device_name,
          start_date: customerForm.warranty_start || null,
          warranty_end_date: customerForm.warranty_end || null,
          status: "active",
        });
      }

      setShowAddCustomer(false);
      setEditingCustomer(null);
      setCustomerForm(emptyCustomerForm);
      loadCustomers();
      if (selected && editingCustomer?.id === selected.id) {
        loadDetail(selected.id);
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Excel Import ────────────────────────────────────────────────────────────
  const [showImport, setShowImport]         = useState(false);
  const [importing, setImporting]           = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult]     = useState<{ added: number; errors: number } | null>(null);
  const importFileRef = useState<HTMLInputElement | null>(null);

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !dealerId) return;
    setImporting(true);
    setImportProgress(0);
    setImportResult(null);

    try {
      const XLSX = await import("xlsx");
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf, { type: "array" });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const raw  = XLSX.utils.sheet_to_json(ws, { defval: "" }) as Record<string, any>[];

      const supabase = getSupabase();
      const BATCH = 20;
      let added = 0; let errors = 0;

      const cleanPhone = (v: any) => {
        if (!v) return "";
        const s = String(typeof v === "number" ? Math.round(v) : v).replace(/\D/g, "");
        return s.slice(0, 11);
      };
      const cleanStr = (v: any) => (v == null || v === "" || String(v).toLowerCase() === "nan") ? "" : String(v).trim();
      const cleanDate = (v: any) => {
        if (!v || String(v).toLowerCase() === "nan") return null;
        try { return new Date(v).toISOString().slice(0, 10); } catch { return null; }
      };

      for (let i = 0; i < raw.length; i += BATCH) {
        const chunk = raw.slice(i, i + BATCH);
        for (const row of chunk) {
          try {
            const fullName = cleanStr(row["AD-SOYAD"] || row["AD SOYAD"] || "");
            if (!fullName) continue;
            const parts = fullName.split(" ");
            const firstName = parts[0] || fullName;
            const lastName  = parts.slice(1).join(" ") || "";
            const fisNo = cleanStr(row["FİŞ NUMARASI"] || "").replace(/\.0$/, "");

            const { data: custData, error: custErr } = await supabase
              .from("crm_customers")
              .insert({
                dealer_id:   dealerId,
                customer_no: fisNo,
                first_name:  firstName,
                last_name:   lastName,
                phone:       cleanPhone(row["TELEFON 1"]),
                phone2:      cleanPhone(row["TELEFON 2"]),
                email:       cleanStr(row["E-MAİL"] || row["EMAIL"] || ""),
                status:      cleanStr(row["DURUM"]) === "Aktif" ? "active" : "passive",
              })
              .select("id")
              .single();

            if (custErr || !custData) { errors++; continue; }
            const custId = (custData as any).id;

            const district = cleanStr(row["İLÇE"] || row["ILCE"] || "");
            const address  = cleanStr(row["ADRES"] || "");
            if (district || address) {
              await supabase.from("crm_addresses").insert({
                customer_id: custId, district, address,
                city: "Antalya", country: "Türkiye", status: "active",
              });
            }

            const deviceName = cleanStr(row["CİHAZ "] || row["CİHAZ"] || row["CIHAZ"] || "");
            if (deviceName) {
              await supabase.from("crm_devices").insert({
                customer_id:      custId,
                device_name:      deviceName,
                start_date:       cleanDate(row["GARANTİ BAŞLANGIÇ"] || row["GARANTİ BASLANGIC"]),
                warranty_end_date: cleanDate(row["GARANTİ BİTİŞ"] || row["GARANTİ BITIS"]),
                status: "active",
              });
            }
            added++;
          } catch { errors++; }
        }
        setImportProgress(Math.round(((i + BATCH) / raw.length) * 100));
        await new Promise((r) => setTimeout(r, 10));
      }

      setImportResult({ added, errors });
      if (added > 0) loadCustomers();
    } catch (err: any) {
      setImportResult({ added: 0, errors: -1 });
    } finally {
      setImporting(false);
      if (e.target) e.target.value = "";
    }
  };

  // ── Save device ─────────────────────────────────────────────────────────────
  const saveDevice = async () => {
    if (!selected || !deviceForm.device_name.trim()) return;
    setSaving(true);
    try {
      const supabase = getSupabase();
      await supabase.from("crm_devices").insert({ ...deviceForm, customer_id: selected.id });
      setShowAddDevice(false);
      setDeviceForm({ serial_no: "", device_code: "", device_name: "", brand: "Bosch", start_date: "", warranty_end_date: "", extended_warranty: false });
      loadDetail(selected.id);
    } finally {
      setSaving(false);
    }
  };

  // ── Save workflow ───────────────────────────────────────────────────────────
  const saveWorkflow = async () => {
    if (!selected || !workflowForm.status_description.trim()) return;
    setSaving(true);
    try {
      const supabase = getSupabase();
      await supabase.from("crm_workflows").insert({ ...workflowForm, customer_id: selected.id });
      setShowAddWorkflow(false);
      setWorkflowForm({ category_type: "Bireysel Müşteri", notification_type: "Kurulum", sub_category: "Yetkili Servis Talebi", flow_type: "Kurulum", status_description: "", serial_no: "" });
      loadDetail(selected.id);
    } finally {
      setSaving(false);
    }
  };

  // ── Save note ───────────────────────────────────────────────────────────────
  const saveNote = async () => {
    if (!selected || !noteText.trim()) return;
    setSaving(true);
    try {
      const supabase = getSupabase();
      const staffName = user?.fullName || user?.username || "Personel";
      await supabase.from("crm_notes").insert({ note: noteText, customer_id: selected.id, created_by: staffName });
      setShowAddNote(false);
      setNoteText("");
      loadDetail(selected.id);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d: string) => d ? new Date(d).toLocaleDateString("tr-TR") : "-";
  const inputClass = "w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400";

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex gap-4 h-full overflow-hidden">

      {/* ── Left: Customer List ── */}
      <div className={`flex flex-col bg-background-50 border border-background-200/70 rounded-xl overflow-hidden transition-all ${selected ? "w-96 shrink-0" : "flex-1"}`}>

        {/* Header */}
        <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between gap-2 shrink-0">
          <div>
            <h4 className="font-semibold text-foreground-950 text-sm">Teknik Servis CRM</h4>
            <p className="text-xs text-foreground-400">{filtered.length} müşteri</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setShowImport(true); setImportResult(null); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-background-50 rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-file-excel-2-line"></i> Excel Yükle
            </button>
            <button
              onClick={() => { setEditingCustomer(null); setCustomerForm(emptyCustomerForm); setShowAddCustomer(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-user-add-line"></i> Yeni Müşteri
            </button>
          </div>
        </div>

        {/* Search & filter */}
        <div className="px-3 py-2 border-b border-background-200/70 flex gap-2 shrink-0">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ad, telefon, e-posta..."
              className="w-full pl-7 pr-3 py-1.5 bg-background-100 border border-background-200/70 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-2 py-1.5 bg-background-100 border border-background-200/70 rounded-lg text-xs cursor-pointer focus:outline-none"
          >
            <option value="all">Tümü</option>
            <option value="active">Aktif</option>
            <option value="passive">Pasif</option>
          </select>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto divide-y divide-background-200/70">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center">
              <i className="ri-user-search-line text-3xl text-foreground-300 block mb-2"></i>
              <p className="text-sm text-foreground-400">Müşteri bulunamadı</p>
            </div>
          ) : (
            filtered.map((c) => (
              <div
                key={c.id}
                className={`flex items-center gap-3 px-4 py-3 hover:bg-background-100/60 transition-colors cursor-pointer ${selected?.id === c.id ? "bg-primary-50 border-r-2 border-primary-500" : ""}`}
              >
                <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center shrink-0 text-sm font-bold text-primary-700">
                  {c.first_name.charAt(0)}{c.last_name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold text-foreground-950 truncate">{c.first_name} {c.last_name}</p>
                      {c.is_vip && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full shrink-0">VIP</span>}
                    </div>
                    <p className="text-xs text-foreground-500">{c.phone || c.phone2 || "-"}</p>
                    {c.customer_no && <p className="text-[10px] text-foreground-400 font-mono">#{c.customer_no}</p>}
                  </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`w-2 h-2 rounded-full ${c.status === "active" ? "bg-emerald-500" : "bg-foreground-300"}`}></span>
                  <button
                    onClick={() => selectCustomer(c)}
                    className="px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-xs font-medium cursor-pointer transition-colors"
                  >
                    Seç
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Right: Customer Detail ── */}
      {selected && (
        <div className="flex-1 flex flex-col bg-background-50 border border-background-200/70 rounded-xl overflow-hidden min-w-0">

          {/* Detail header */}
          <div className="px-5 py-3.5 border-b border-background-200/70 flex items-center justify-between gap-3 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <button onClick={() => setSelected(null)} className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 cursor-pointer shrink-0">
                <i className="ri-arrow-left-line"></i>
              </button>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h5 className="font-bold text-foreground-950">{selected.first_name} {selected.last_name}</h5>
                  {selected.is_vip && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">VIP</span>}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${selected.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-foreground-100 text-foreground-500"}`}>
                    {selected.status === "active" ? "Aktif" : "Pasif"}
                  </span>
                </div>
                <p className="text-xs text-foreground-400">Müşteri No: {selected.customer_no || selected.id.slice(0, 8).toUpperCase()}</p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditingCustomer(selected);
                setCustomerForm({
                  customer_no: selected.customer_no,
                  first_name: selected.first_name,
                  last_name: selected.last_name,
                  phone: selected.phone,
                  phone2: selected.phone2,
                  email: selected.email,
                  status: selected.status,
                  is_vip: selected.is_vip,
                  district: addresses[0]?.district || "",
                  address: addresses[0]?.address || "",
                  device_name: devices[0]?.device_name || "",
                  warranty_start: devices[0]?.start_date || "",
                  warranty_end: devices[0]?.warranty_end_date || "",
                  quantity: "",
                });
                setShowAddCustomer(true);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-xs font-medium cursor-pointer hover:bg-background-200/70 shrink-0"
            >
              <i className="ri-pencil-line"></i> Düzenle
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-background-200/70 overflow-x-auto shrink-0">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  activeTab === tab.id
                    ? "border-b-2 border-primary-500 text-primary-700 bg-primary-50/40"
                    : "text-foreground-500 hover:bg-background-100"
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto">
            {detailLoading ? (
              <div className="flex items-center justify-center py-16">
                <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
              </div>
            ) : (

              <>
                {/* ── Müşteri Bilgileri ── */}
                {activeTab === "info" && (
                  <div className="p-5 grid md:grid-cols-2 gap-5">
                    <div className="space-y-4">
                      <h6 className="text-xs font-bold text-foreground-500 uppercase tracking-wider border-b border-background-200/70 pb-2">Kişisel Bilgiler</h6>
                      {[
                        { label: "Müşteri No",   val: selected.customer_no || "-" },
                        { label: "Ad",           val: selected.first_name },
                        { label: "Soyad",        val: selected.last_name },
                        { label: "Telefon",      val: selected.phone || "-" },
                        { label: "2. Telefon",   val: selected.phone2 || "-" },
                        { label: "E-posta",      val: selected.email || "-" },
                        { label: "Kayıt Tarihi", val: formatDate(selected.created_at) },
                      ].map((row) => (
                        <div key={row.label} className="flex justify-between text-sm border-b border-background-100 pb-2">
                          <span className="text-foreground-400 font-medium">{row.label}</span>
                          <span className="text-foreground-950 font-medium text-right">{row.val}</span>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-4">
                      <h6 className="text-xs font-bold text-foreground-500 uppercase tracking-wider border-b border-background-200/70 pb-2">Durum Bilgileri</h6>
                      <div className="flex justify-between text-sm border-b border-background-100 pb-2">
                        <span className="text-foreground-400 font-medium">Durum</span>
                        <span className={`font-semibold ${selected.status === "active" ? "text-emerald-600" : "text-foreground-400"}`}>
                          {selected.status === "active" ? "Aktif" : "Pasif"}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm border-b border-background-100 pb-2">
                        <span className="text-foreground-400 font-medium">VIP Müşteri</span>
                        <span className="font-semibold">{selected.is_vip ? "✓ Evet" : "Hayır"}</span>
                      </div>
                      <div className="flex justify-between text-sm border-b border-background-100 pb-2">
                        <span className="text-foreground-400 font-medium">Toplam Cihaz</span>
                        <span className="font-semibold text-primary-600">{devices.length}</span>
                      </div>
                      <div className="flex justify-between text-sm border-b border-background-100 pb-2">
                        <span className="text-foreground-400 font-medium">Toplam İş Emri</span>
                        <span className="font-semibold text-primary-600">{workflows.length}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Adres ── */}
                {activeTab === "address" && (
                  <div className="p-5">
                    {addresses.length === 0 ? (
                      <div className="py-12 text-center text-foreground-400">
                        <i className="ri-map-pin-line text-3xl block mb-2 text-foreground-300"></i>
                        <p className="text-sm">Adres kaydı yok</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {addresses.map((addr) => (
                          <div key={addr.id} className="bg-background-100 border border-background-200/70 rounded-xl p-4">
                            <div className="flex items-start justify-between mb-2">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${addr.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-foreground-100 text-foreground-500"}`}>
                                {addr.status === "active" ? "Aktif" : "Pasif"}
                              </span>
                              <span className="text-xs text-foreground-400">{addr.ownership_type || "-"}</span>
                            </div>
                            <p className="text-sm font-medium text-foreground-950 mb-1">{addr.address || "-"}</p>
                            <p className="text-xs text-foreground-500">
                              {[addr.region, addr.district, addr.city, addr.country].filter(Boolean).join(" / ")}
                            </p>
                            {addr.apartment_no && <p className="text-xs text-foreground-400 mt-0.5">Daire: {addr.apartment_no} {addr.flat ? `· Kat: ${addr.flat}` : ""}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Cihazlar ── */}
                {activeTab === "devices" && (
                  <div className="p-5">
                    <div className="flex justify-end mb-3">
                      <button onClick={() => setShowAddDevice(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-xs font-medium cursor-pointer">
                        <i className="ri-add-line"></i> Cihaz Ekle
                      </button>
                    </div>
                    {devices.length === 0 ? (
                      <div className="py-12 text-center text-foreground-400">
                        <i className="ri-settings-3-line text-3xl block mb-2 text-foreground-300"></i>
                        <p className="text-sm">Cihaz kaydı yok</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs min-w-[600px]">
                          <thead className="bg-background-100">
                            <tr>
                              {["Seri No", "Cihaz Kodu", "Cihaz Adı", "Marka", "Başlangıç", "Garanti Bitiş", "Genişl. Garanti", "Durum"].map((h) => (
                                <th key={h} className="px-3 py-2 text-left text-[11px] font-medium text-foreground-500">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {devices.map((d) => (
                              <tr key={d.id} className="border-t border-background-200/70 hover:bg-background-100/40">
                                <td className="px-3 py-2 font-mono text-foreground-600">{d.serial_no || "-"}</td>
                                <td className="px-3 py-2 text-foreground-600">{d.device_code || "-"}</td>
                                <td className="px-3 py-2 font-medium text-foreground-950">{d.device_name}</td>
                                <td className="px-3 py-2 text-foreground-500">{d.brand || "-"}</td>
                                <td className="px-3 py-2 text-foreground-500">{formatDate(d.start_date)}</td>
                                <td className="px-3 py-2 text-foreground-500">{formatDate(d.warranty_end_date)}</td>
                                <td className="px-3 py-2">
                                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${d.extended_warranty ? "bg-emerald-100 text-emerald-700" : "bg-foreground-100 text-foreground-500"}`}>
                                    {d.extended_warranty ? "Evet" : "Hayır"}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${d.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-foreground-100 text-foreground-500"}`}>
                                    {d.status === "active" ? "Aktif" : "Pasif"}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* ── İş Emirleri ── */}
                {activeTab === "workflow" && (
                  <div className="p-5">
                    <div className="flex justify-end mb-3">
                      <button onClick={() => setShowAddWorkflow(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-xs font-medium cursor-pointer">
                        <i className="ri-add-line"></i> İş Emri Ekle
                      </button>
                    </div>
                    {workflows.length === 0 ? (
                      <div className="py-12 text-center text-foreground-400">
                        <i className="ri-clipboard-line text-3xl block mb-2 text-foreground-300"></i>
                        <p className="text-sm">İş emri kaydı yok</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {workflows.map((wf) => (
                          <div key={wf.id} className="bg-background-100 border border-background-200/70 rounded-xl p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex flex-wrap gap-1.5">
                                <span className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">{wf.category_type}</span>
                                <span className="text-[10px] px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full font-medium">{wf.notification_type}</span>
                                <span className="text-[10px] px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full font-medium">{wf.flow_type}</span>
                              </div>
                              <span className="text-xs text-foreground-400 shrink-0">{formatDate(wf.created_at)}</span>
                            </div>
                            <p className="text-sm text-foreground-800">{wf.status_description}</p>
                            {wf.serial_no && <p className="text-xs text-foreground-400 mt-1"><i className="ri-barcode-line mr-1"></i>Seri No: {wf.serial_no}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Notlar ── */}
                {activeTab === "notes" && (
                  <div className="p-5">
                    <div className="flex justify-end mb-3">
                      <button onClick={() => setShowAddNote(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-xs font-medium cursor-pointer">
                        <i className="ri-add-line"></i> Not Ekle
                      </button>
                    </div>
                    {notes.length === 0 ? (
                      <div className="py-12 text-center text-foreground-400">
                        <i className="ri-file-text-line text-3xl block mb-2 text-foreground-300"></i>
                        <p className="text-sm">Not kaydı yok</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {notes.map((note) => (
                          <div key={note.id} className="bg-background-100 border border-background-200/70 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-foreground-600 flex items-center gap-1">
                                <i className="ri-user-star-line"></i> {note.created_by}
                              </span>
                              <span className="text-xs text-foreground-400">{formatDate(note.created_at)}</span>
                            </div>
                            <p className="text-sm text-foreground-800">{note.note}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Add/Edit Customer Modal ── */}
      {showAddCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowAddCustomer(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h4 className="text-lg font-semibold text-foreground-950">{editingCustomer ? "Müşteri Düzenle" : "Yeni Müşteri"}</h4>
              <button onClick={() => setShowAddCustomer(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Fiş Numarası</label>
                  <input type="text" value={customerForm.customer_no} onChange={(e) => setCustomerForm({ ...customerForm, customer_no: e.target.value })} placeholder="Örn: 4248805" className={inputClass} />
                </div>
                <div className="flex items-end gap-3 pb-0.5">
                  <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="checkbox" checked={customerForm.is_vip} onChange={(e) => setCustomerForm({ ...customerForm, is_vip: e.target.checked })} className="w-4 h-4" /> VIP</label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="checkbox" checked={customerForm.status === "active"} onChange={(e) => setCustomerForm({ ...customerForm, status: e.target.checked ? "active" : "passive" })} className="w-4 h-4" /> Aktif</label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Ad *</label>
                  <input type="text" value={customerForm.first_name} onChange={(e) => setCustomerForm({ ...customerForm, first_name: e.target.value })} autoFocus className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Soyad *</label>
                  <input type="text" value={customerForm.last_name} onChange={(e) => setCustomerForm({ ...customerForm, last_name: e.target.value })} className={inputClass} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Telefon 1</label>
                  <input type="tel" value={customerForm.phone} onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Telefon 2</label>
                  <input type="tel" value={customerForm.phone2} onChange={(e) => setCustomerForm({ ...customerForm, phone2: e.target.value })} className={inputClass} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">E-Mail</label>
                <input type="email" value={customerForm.email} onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} className={inputClass} />
              </div>
              <div className="border-t border-background-200/70 pt-3">
                <p className="text-xs font-bold text-foreground-500 mb-2 uppercase tracking-wider">Adres Bilgileri</p>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="col-span-1">
                    <label className="block text-xs font-medium text-foreground-500 mb-1">İlçe</label>
                    <input type="text" value={customerForm.district} onChange={(e) => setCustomerForm({ ...customerForm, district: e.target.value })} placeholder="Kepez" className={inputClass} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Adres</label>
                    <input type="text" value={customerForm.address} onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })} placeholder="Mahalle, sokak, no..." className={inputClass} />
                  </div>
                </div>
              </div>
              <div className="border-t border-background-200/70 pt-3">
                <p className="text-xs font-bold text-foreground-500 mb-2 uppercase tracking-wider">Cihaz Bilgileri</p>
                <div className="mb-3">
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Cihaz</label>
                  <input type="text" value={customerForm.device_name} onChange={(e) => setCustomerForm({ ...customerForm, device_name: e.target.value })} placeholder="Örn: GC1200W 20/22 C 23 (Kombi)" className={inputClass} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Garanti Başlangıç</label>
                    <input type="date" value={customerForm.warranty_start} onChange={(e) => setCustomerForm({ ...customerForm, warranty_start: e.target.value })} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Garanti Bitiş</label>
                    <input type="date" value={customerForm.warranty_end} onChange={(e) => setCustomerForm({ ...customerForm, warranty_end: e.target.value })} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Adet</label>
                    <input type="number" value={customerForm.quantity} onChange={(e) => setCustomerForm({ ...customerForm, quantity: e.target.value })} placeholder="1" className={inputClass} />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowAddCustomer(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm cursor-pointer">İptal</button>
              <button onClick={saveCustomer} disabled={saving} className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer disabled:opacity-50">
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Device Modal ── */}
      {showAddDevice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowAddDevice(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h4 className="text-lg font-semibold text-foreground-950">Cihaz Ekle</h4>
              <button onClick={() => setShowAddDevice(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Cihaz Adı *</label>
                <input type="text" value={deviceForm.device_name} onChange={(e) => setDeviceForm({ ...deviceForm, device_name: e.target.value })} autoFocus placeholder="Örn: GB062-20 KD H (Kombi)" className={inputClass} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Seri No</label>
                  <input type="text" value={deviceForm.serial_no} onChange={(e) => setDeviceForm({ ...deviceForm, serial_no: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Cihaz Kodu</label>
                  <input type="text" value={deviceForm.device_code} onChange={(e) => setDeviceForm({ ...deviceForm, device_code: e.target.value })} className={inputClass} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Marka</label>
                <select value={deviceForm.brand} onChange={(e) => setDeviceForm({ ...deviceForm, brand: e.target.value })} className={inputClass + " cursor-pointer"}>
                  {["Bosch", "Buderus", "Hitachi", "York", "Diğer"].map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Başlangıç Tarihi</label>
                  <input type="date" value={deviceForm.start_date} onChange={(e) => setDeviceForm({ ...deviceForm, start_date: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Garanti Bitiş</label>
                  <input type="date" value={deviceForm.warranty_end_date} onChange={(e) => setDeviceForm({ ...deviceForm, warranty_end_date: e.target.value })} className={inputClass} />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={deviceForm.extended_warranty} onChange={(e) => setDeviceForm({ ...deviceForm, extended_warranty: e.target.checked })} className="w-4 h-4" />
                Genişletilmiş Garanti
              </label>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowAddDevice(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm cursor-pointer">İptal</button>
              <button onClick={saveDevice} disabled={saving} className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer disabled:opacity-50">
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Workflow Modal ── */}
      {showAddWorkflow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowAddWorkflow(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h4 className="text-lg font-semibold text-foreground-950">İş Emri Ekle</h4>
              <button onClick={() => setShowAddWorkflow(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Kategori</label>
                  <select value={workflowForm.category_type} onChange={(e) => setWorkflowForm({ ...workflowForm, category_type: e.target.value })} className={inputClass + " cursor-pointer"}>
                    {["Bireysel Müşteri", "Kurumsal Müşteri"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Bildirim Türü</label>
                  <select value={workflowForm.notification_type} onChange={(e) => setWorkflowForm({ ...workflowForm, notification_type: e.target.value })} className={inputClass + " cursor-pointer"}>
                    {["Kurulum", "Arıza", "Bakım", "Bilgi", "Şikayet"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Alt Kategori</label>
                  <select value={workflowForm.sub_category} onChange={(e) => setWorkflowForm({ ...workflowForm, sub_category: e.target.value })} className={inputClass + " cursor-pointer"}>
                    {["Yetkili Servis Talebi", "Garanti Talebi", "Ücretli Servis", "Periyodik Bakım"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Akış Türü</label>
                  <select value={workflowForm.flow_type} onChange={(e) => setWorkflowForm({ ...workflowForm, flow_type: e.target.value })} className={inputClass + " cursor-pointer"}>
                    {["Kurulum", "Arıza Giderme", "Bakım", "Kontrol"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Seri No</label>
                <input type="text" value={workflowForm.serial_no} onChange={(e) => setWorkflowForm({ ...workflowForm, serial_no: e.target.value })} placeholder="Cihaz seri numarası" className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Durum / Açıklama *</label>
                <textarea value={workflowForm.status_description} onChange={(e) => setWorkflowForm({ ...workflowForm, status_description: e.target.value })} rows={3} autoFocus placeholder="İş emri detayları..." className={inputClass + " resize-none"} />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowAddWorkflow(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm cursor-pointer">İptal</button>
              <button onClick={saveWorkflow} disabled={saving} className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer disabled:opacity-50">
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Note Modal ── */}
      {showAddNote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowAddNote(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-foreground-950">Not Ekle</h4>
              <button onClick={() => setShowAddNote(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={5}
              autoFocus
              placeholder="Müşteri notu..."
              className={inputClass + " resize-none"}
            />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowAddNote(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm cursor-pointer">İptal</button>
              <button onClick={saveNote} disabled={saving} className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer disabled:opacity-50">
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Excel Import Modal ── */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => !importing && setShowImport(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h4 className="text-lg font-semibold text-foreground-950">Excel'den Müşteri Yükle</h4>
              {!importing && <button onClick={() => setShowImport(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>}
            </div>

            {!importing && !importResult && (
              <>
                <div className="bg-background-100 rounded-xl p-4 mb-4 text-xs text-foreground-500 space-y-1">
                  <p className="font-semibold text-foreground-700 mb-2">Beklenen sütun başlıkları:</p>
                  <p>FİŞ NUMARASI · AD-SOYAD · TELEFON 1 · TELEFON 2</p>
                  <p>E-MAİL · İLÇE · ADRES · DURUM</p>
                  <p>CİHAZ · GARANTİ BİTİŞ · GARANTİ BAŞLANGIÇ · ADET</p>
                </div>
                <label className="flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed border-background-300 rounded-xl cursor-pointer hover:border-primary-400 hover:bg-primary-50/20 transition-colors">
                  <i className="ri-file-excel-2-line text-4xl text-emerald-500"></i>
                  <span className="text-sm font-medium text-foreground-700">Excel dosyası seçin</span>
                  <span className="text-xs text-foreground-400">.xlsx, .xls</span>
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportFile} />
                </label>
              </>
            )}

            {importing && (
              <div className="text-center py-6">
                <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 block mb-3"></i>
                <p className="font-medium text-foreground-950 mb-1">Müşteriler yükleniyor...</p>
                <p className="text-sm text-foreground-400 mb-4">Lütfen bekleyin, sayfayı kapatmayın</p>
                <div className="w-full bg-background-200 rounded-full h-2.5">
                  <div className="bg-primary-500 h-2.5 rounded-full transition-all duration-300" style={{ width: `${importProgress}%` }}></div>
                </div>
                <p className="text-xs text-foreground-400 mt-2">%{importProgress}</p>
              </div>
            )}

            {importResult && (
              <div className="text-center py-4">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 ${importResult.errors === -1 ? "bg-accent-100" : "bg-emerald-100"}`}>
                  <i className={`text-2xl ${importResult.errors === -1 ? "ri-error-warning-line text-accent-600" : "ri-checkbox-circle-fill text-emerald-500"}`}></i>
                </div>
                {importResult.errors === -1 ? (
                  <p className="font-semibold text-accent-700">Dosya okunamadı. Lütfen formatı kontrol edin.</p>
                ) : (
                  <>
                    <p className="font-bold text-foreground-950 text-lg mb-1">{importResult.added} müşteri eklendi</p>
                    {importResult.errors > 0 && <p className="text-sm text-amber-600">{importResult.errors} kayıt atlandı</p>}
                  </>
                )}
                <button onClick={() => setShowImport(false)} className="mt-5 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer">Kapat</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
